import React, { useEffect } from 'react';
import { connect } from 'react-redux';
import { useLanguage } from '@ipc/ipc-core';
import { useHistory, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import AppBar from '@material-ui/core/AppBar';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { bindActionCreators } from 'redux';
import MrdMonitoringReport from '../mrd-monitoring/component/MrdMonitoringReport';
import MrdAdministration from '../mrd-administration/component/MrdAdministration';
import { getUserRole } from '../../common/user/redux/selectors/UserSelectors';
import { getBlockRouting, getUserConfirmed } from '../../common/redux/selectors/redirectSelector';
import { triggerConfirmationDialogAction, triggerRedirect } from '../../common/redux/actions/redirectActions';
import { clearNotification } from '../../common/notifications/redux/actions/NotificationActions';
import { setTableActiveFilter } from '../../common/list-table/actions/ListTableActions';

function GeneralTabsPanel(props) {
  const {
    children,
    value,
    index,
    ...other
  } = props;

  return (
    <Typography
      component="div"
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={3}>{children}</Box>}
    </Typography>
  );
}

GeneralTabsPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    backgroundColor: theme.palette.background.paper,
    overflow: 'scroll',
  },
}));

function SimpleTabs(props) {
  const classes = useStyles();
  const [, language] = useLanguage();
  const [blockedValue, blockValue] = React.useState('');
  const history = useHistory();
  const location = useLocation();

  const {
    userRole,
    routeBlocker,
    setAlert,
    isUserConfirmed,
    resetConfirmation,
    withURL,
    resetNotification,
    setTableFilter,
  } = props;

  let defaultValue = '/monitoring';
  if (withURL && withURL.startsWith('/administration')) defaultValue = '/administration';

  const [value, setValue] = React.useState(defaultValue);

  const updateValue = (newValue) => {
    const { pathname } = location;
    setValue(newValue);
    resetNotification();
    if (pathname !== language(`${newValue}`)) {
      history.push(newValue);
    }
  };

  const userRoles = ['IPC_ADMIN', 'IPC_VIEWER', 'OPER_ADMIN', 'OPER_VIEWER'];

  useEffect(() => {
    if (!routeBlocker && blockedValue !== '' && isUserConfirmed) {
      updateValue(blockedValue);
      resetConfirmation(false);
      blockValue('');
    }
  }, [isUserConfirmed]);

  useEffect(() => {
    if (withURL && !withURL.startsWith(value)) {
      updateValue(withURL.startsWith('/administration') ? '/administration' : '/monitoring');
    }
  }, [withURL]);

  const handleChange = (event, newValue) => {
    if (!routeBlocker) {
      updateValue(newValue);
      setTableFilter();
    } else {
      blockValue(newValue);
      setAlert(true);
    }
  };

  return (
    <div className={classes.root}>
      <AppBar position="static" color="default">
        <Tabs value={value} onChange={handleChange} aria-label="simple tabs example">
          <Tab label="" value="/" className="hidden-tab" />
          <Tab label="MRD Monitoring" {...a11yProps(0)} value="/monitoring" />
          <Tab label="MRD Administration" {...a11yProps(1)} value="/administration" />
        </Tabs>
      </AppBar>
      <GeneralTabsPanel value={value} index="/monitoring">
        {userRoles.includes(userRole) && (
          <MrdMonitoringReport withURL={withURL ? withURL.split(value)[1] : ''} />
        )}
      </GeneralTabsPanel>
      <GeneralTabsPanel value={value} index="/administration">
        {userRoles.includes(userRole) && (
          <MrdAdministration withURL={withURL ? withURL.split(value)[1] : ''} />
        )}
      </GeneralTabsPanel>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    userRole: getUserRole(state),
    routeBlocker: getBlockRouting(state),
    isUserConfirmed: getUserConfirmed(state),
  };
}

const mapDispatchToProps = (dispatch) => bindActionCreators({
  setAlert: triggerConfirmationDialogAction,
  resetConfirmation: triggerRedirect,
  resetNotification: clearNotification,
  setTableFilter: setTableActiveFilter,
}, dispatch);

SimpleTabs.propTypes = {
  userRole: PropTypes.string,
  routeBlocker: PropTypes.bool,
  setAlert: PropTypes.func,
  isUserConfirmed: PropTypes.bool,
  resetConfirmation: PropTypes.func,
  withURL: PropTypes.string,
  resetNotification: PropTypes.func,
  setTableFilter: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(SimpleTabs);
